import { FC, forwardRef, useRef, type Ref } from "react";
import { ToggleSwitchComponentProps } from "../../interface";
import { styled } from "@mui/material/styles";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch, { type SwitchProps } from "@mui/material/Switch";

// `isChecked` is the Syncfusion spelling of MUI's `checked`; both are accepted.
const SwitchComponent = styled(({ isChecked, checked, ...rest }: SwitchProps & { isChecked?: boolean }) => {
  const checkedProps = isChecked !== undefined || checked !== undefined ? { checked: !!(isChecked || checked) } : {};
  return <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...checkedProps} {...rest} />;
})(({ theme }) => ({
  width: "2.75rem",
  height: "1.25rem",
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "0.25rem",
    transitionDuration: "500ms",
    "&.Mui-checked:hover .MuiSwitch-thumb": {
      background: "black !important",
    },

    "&.Mui-checked": {
      transform: "translateX(1.375rem)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        background: "linear-gradient(100.44deg, var(--gradient-color) 0%, var(--primary-gradient-color2) 110.4%)",
        opacity: 1,
        border: 0,
      },

      "&.Mui-disabled + .MuiSwitch-track": {
        background: " var(--Icon-icon-secondary, var(--text-light))",
      },

      "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        color: "white !important",
        background: "white !important",
        width: "0.75rem",
        height: "0.75rem",
      },
    },
    "&:not(.Mui-checked):hover .MuiSwitch-thumb": {
      background:
        "linear-gradient(100.44deg, var(--gradient-color) 0%, var(--primary-gradient-color2) 110.4%) !important",
    },

    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "0.375rem solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      background: "#B3B4C1",
      color: "#B3B4C1",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
      // ...theme.applyStyles('dark', {
      //   opacity: 0.3,
      // }),
      border: "1px solid var(--text-light)",
    },
  },

  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    color: "black",
    width: "0.75rem",
    height: "0.75rem",
  },

  "& .MuiSwitch-track": {
    borderRadius: "0.65625rem",
    backgroundColor: "white",
    opacity: 1,
    border: "1px solid var(--system-colour-47)",
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));

// `labelPlacement` is read here but not declared on the shared props type.
type ToggleSwitchAllProps = ToggleSwitchComponentProps & {
  labelPlacement?: "end" | "start" | "top" | "bottom";
};

const ToggleSwitchComponent: FC<ToggleSwitchAllProps> = forwardRef(
  (props: ToggleSwitchAllProps, ref: Ref<HTMLInputElement>) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const toggleSwitchRef = ref || internalRef;

    return (
      <div className="flex items-center gap-1">
        <FormGroup>
          <FormControlLabel
            sx={{ marginInlineStart: 0 }}
            control={
              // @ts-expect-error -- the wrapper declares its focus/blur handlers over `HTMLInputElement` but forwards them to a MUI control typed over `HTMLButtonElement`. The two element types are genuinely different; reconciling them means changing which DOM node the wrapper renders.
              <SwitchComponent
                {...props}
                onChange={props.onChange || props.change}
                inputRef={toggleSwitchRef}
                sx={{ m: "0.5rem" }}
                disabled={props.isDisabled || props.disabled}
              />
            }
            label={props.label}
            labelPlacement={props.labelPlacement || "start"}
          />
        </FormGroup>
      </div>
    );
  },
);
ToggleSwitchComponent.displayName = "ToggleSwitchComponent";
export default ToggleSwitchComponent;
